import { useEffect, useState } from "preact/hooks"
import { Database, Loader2 } from "lucide-react"
import { api_fetch } from "../../api/client"
import { deriveSyndicatedPromotionEffectiveness } from "../../api/promotion_effectiveness"
import { Button } from "../../components/ui/button"
import { Label } from "../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { toast } from "../../components/ui/use-toast"
import { classes } from "../../utils"
import { CalendarInfo } from "../trade_promotion_management/promo_creation/types"

interface SyndicatedMetricsImportProps {
  report_id: string
  className?: string
}

export function SyndicatedMetricsImport({ report_id, className }: SyndicatedMetricsImportProps) {
  const [calendars, set_calendars] = useState<CalendarInfo[]>([])
  const [selected_calendar_id, set_selected_calendar_id] = useState("")
  const [loading_calendars, set_loading_calendars] = useState(false)
  const [deriving, set_deriving] = useState(false)

  useEffect(() => {
    load_calendars()
  }, [])

  useEffect(() => {
    if (!selected_calendar_id && calendars.length > 0) {
      set_selected_calendar_id(calendars[0].id)
    }
  }, [calendars, selected_calendar_id])

  async function load_calendars() {
    set_loading_calendars(true)
    try {
      const res = await api_fetch<CalendarInfo[]>("/promotional-calendars", {
        params: { include_promotions: false },
      })
      if (res.ok) {
        const metadata = res.value.data.map((calendar_row: CalendarInfo) => ({ ...calendar_row, promotions: [] }))
        set_calendars(metadata)
      }
    } catch (error) {
      console.error("Failed to load promotional calendars:", error)
    } finally {
      set_loading_calendars(false)
    }
  }

  async function handle_derive_syndicated_metrics() {
    if (!selected_calendar_id || !report_id) return
    set_deriving(true)
    try {
      const result = await deriveSyndicatedPromotionEffectiveness(selected_calendar_id, report_id)
      toast({
        title: "Syndicated metrics imported",
        description: `${result.imported_rows} promotion metric row${result.imported_rows === 1 ? "" : "s"} created from syndicated data.`,
      })
    } catch (error) {
      toast({
        title: "Syndicated import failed",
        description: error instanceof Error ? error.message : "Could not derive promotion metrics.",
        variant: "destructive",
      })
    } finally {
      set_deriving(false)
    }
  }

  return (
    <div class={classes("flex flex-wrap items-end gap-4", className)}>
      <div class="w-56">
        <Label class="mb-2 block">Promotion calendar</Label>
        <Select
          value={selected_calendar_id}
          onValueChange={set_selected_calendar_id}
          disabled={loading_calendars || calendars.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select calendar" />
          </SelectTrigger>
          <SelectContent>
            {calendars.map((calendar: CalendarInfo) => (
              <SelectItem key={calendar.id} value={calendar.id}>
                {calendar.calendar_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label class="mb-2 block invisible">Pull</Label>
        <Button
          onClick={handle_derive_syndicated_metrics}
          disabled={!report_id || !selected_calendar_id || deriving}
          variant="outline"
        >
          {deriving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
          Pull syndicated metrics
        </Button>
      </div>
    </div>
  )
}
